import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Cloud, Monitor, Server, Camera, Lightbulb, CheckCircle2 } from 'lucide-react';
import { MicrosoftBadge, AzureBadge } from '../../components/PartnerBadges';
import { Navbar } from '../../components/Navbar';
import { SEO, serviceSchema, faqSchema, breadcrumbSchema } from '../../components/SEO';
import { FAQSection } from './PillarPage';

const IT_THEME = { primary: '#2563EB', accent: '#3B82F6', soft: '#EFF6FF' };

const IT_FAQS = [
  {
    question: 'What does cloud and DevOps transformation include?',
    answer: 'It covers cloud architecture and migration from on-premises to AWS, Azure, or GCP, hybrid cloud design, infrastructure-as-code with Terraform, CI/CD pipeline implementation, monitoring and observability, and cost optimization. The goal is faster, safer deployments with full automation.',
  },
  {
    question: 'How do you price managed IT support?',
    answer: 'We tailor pricing to the scope of coverage — helpdesk only, full managed services, or anything in between. Pricing is based on your user count, device fleet, and support requirements. We will put together a clear proposal after a short discovery call so you know exactly what is covered.',
  },
  {
    question: 'How long does a cloud migration take?',
    answer: 'A straightforward lift-and-shift of a small infrastructure takes 4 to 8 weeks. Re-architecture for cloud-native design, containerization, or multi-region deployments typically takes 3 to 6 months. We always start with a discovery and assessment phase to give you a realistic timeline and cost estimate before any migration work begins.',
  },
  {
    question: 'Do you provide managed IT support for businesses?',
    answer: 'Yes. We provide full managed IT services including endpoint management, helpdesk support, patch management, antivirus and EDR deployment, backup and disaster recovery, and 24/7 monitoring for small to mid-size businesses.',
  },
  {
    question: 'Which cloud platforms do you work with?',
    answer: 'We work across AWS, Microsoft Azure, and Google Cloud Platform. Most of our enterprise clients run on Azure given our Microsoft Partner status, but we have delivered production workloads on all three. We also manage hybrid environments — on-premises plus cloud — for businesses not ready to fully migrate.',
  },
  {
    question: 'Are you a Microsoft and Azure partner?',
    answer: 'Yes. Smart Solutions is a Microsoft Partner with experience deploying Microsoft 365, Azure infrastructure, Entra ID, Intune, and the Microsoft Defender security stack for businesses of all sizes.',
  },
  {
    question: 'Can you reduce our cloud infrastructure costs?',
    answer: 'Usually yes. Common wins include right-sizing over-provisioned VMs, switching to reserved instances or savings plans, removing idle resources, optimizing storage tiers, and moving eligible workloads to PaaS or serverless. We have helped clients cut cloud spend by 20 to 45% without degrading performance.',
  },
  {
    question: 'Can you set up surveillance and physical security systems?',
    answer: 'Yes. We design and install IP camera systems, access control, network video recorders (NVR), and integrate them with your existing IT infrastructure for centralized monitoring and remote access.',
  },
  {
    question: 'What is your approach to IT disaster recovery?',
    answer: 'We design recovery strategies around your RTO (how fast you need to be back up) and RPO (how much data loss is acceptable). This includes automated backups to geographically separate locations, failover testing, documented runbooks, and regular drills. We also set up cloud-based disaster recovery environments so you can fail over without owning redundant hardware.',
  },
  {
    question: 'Do you support remote and hybrid workforces?',
    answer: 'Yes. We deploy and manage the full remote-work stack — Microsoft 365 or Google Workspace, VPN or zero-trust network access (ZTNA), device management (Intune, Jamf), endpoint security, and collaboration tools — so your team works securely from anywhere.',
  },
  {
    question: 'Do you offer IT consulting and strategy?',
    answer: 'Yes. We provide IT strategy consulting covering technology roadmaps, vendor selection, cloud migration planning, security posture assessment, and budget optimization tailored to your business goals.',
  },
];

const serviceBlocks = [
  {
    id: '01',
    icon: Cloud,
    color: '#2563EB',
    bg: '#eef0eb',
    title: 'Cloud & DevOps Transformation',
    description:
      'We design, implement, and manage scalable cloud environments tailored to your business needs. Our DevOps practices ensure faster deployments, improved team collaboration, and fully automated continuous delivery pipelines.',
    offerings: [
      'Cloud Architecture & Migration (on-prem to cloud)',
      'Azure & hybrid cloud architecture',
      'Cloud Infrastructure optimization',
      'CI/CD pipeline implementation',
      'Infrastructure as Code (IaC)',
      'Monitoring, automation, and performance optimization',
    ],
  },
  {
    id: '02',
    icon: Monitor,
    color: '#00A4EF',
    bg: '#EFF6FF',
    title: 'Microsoft Solutions & Modern Workplace',
    isMicrosoft: true,
    description:
      'As specialists in Microsoft technologies, we empower organizations with secure, collaborative, and productive digital workplaces. From deployment to ongoing management, we handle the full Microsoft ecosystem.',
    offerings: [
      'Microsoft 365 deployment & management',
      'Azure cloud services & Identity & Access Management (IAM)',
      'SharePoint & Teams collaboration solutions',
      'Endpoint management (Intune)',
      'Security solutions (Microsoft Defender)',
      'Licensing optimization',
    ],
  },
  {
    id: '03',
    icon: Server,
    color: '#3B82F6',
    bg: '#eef0eb',
    title: 'IT Infrastructure & Data Center Solutions',
    description:
      'We provide end-to-end infrastructure solutions that ensure your IT environment is robust, scalable, and future-ready — from on-premises data centers to fully hybrid cloud environments.',
    offerings: [
      'Server & storage solutions',
      'Virtualization (VMware, Hyper-V)',
      'Backup & disaster recovery planning',
      'High availability & failover solutions',
      'Data center setup and optimization',
      'Business Continuity (DRaaS)',
      'On-Prem to Cloud Hybridity',
    ],
  },
  {
    id: '04',
    icon: Camera,
    color: '#3B82F6',
    bg: '#eef0eb',
    title: 'Smart Surveillance & Physical Security',
    description:
      'We design and deploy advanced surveillance systems to enhance safety, monitoring, and operational visibility across your facilities — integrated with AI-powered analytics for intelligent threat detection.',
    offerings: [
      'CCTV installation & configuration',
      'AI-Powered video analytics',
      'IP camera systems',
      'Remote monitoring solutions',
      'Video analytics & storage solutions',
      'Maintenance and upgrades',
    ],
  },
  {
    id: '05',
    icon: Lightbulb,
    color: '#059669',
    bg: '#ECFDF5',
    title: 'IT Consulting & Digital Transformation',
    description:
      'We help organizations align technology with business goals, enabling smarter decisions, improved efficiency, and long-term growth. Our consulting approach transforms traditional IT environments into modern, agile, and scalable digital ecosystems.',
    offerings: [
      'IT strategy & roadmap planning',
      'Digital transformation planning',
      'Cloud adoption strategy',
      'Business Process Automation',
      'IT cost optimization & assessment',
      'Process automation & modernization',
      'Governance, risk, and compliance advisory',
    ],
  },
];

export const ITServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="IT Services, Cloud & Managed IT Support"
        description="IT services covering cloud and DevOps transformation, managed IT support, Microsoft 365 and Azure deployment, surveillance, and IT strategy consulting for growing businesses."
        path="/services/it-solutions"
        jsonLd={[
          serviceSchema({
            name: 'IT Services & Cloud Solutions',
            description: 'Cloud transformation, managed IT services, Microsoft and Azure deployments, surveillance, and IT consulting from a certified Microsoft Partner.',
            path: '/services/it-solutions',
          }),
          faqSchema(IT_FAQS),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/#services' },
            { name: 'IT & Cloud Solutions', path: '/services/it-solutions' },
          ]),
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-white border-b border-[#E2E8F0]">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full bg-[#2563EB]/5 blur-[130px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#3B82F6]/5 blur-[110px] pointer-events-none" />
        <div className="container-custom py-20 lg:py-28 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">IT & Cloud Solutions</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-6 leading-tight"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}
            >
              Enterprise IT,<br />
              <span
                className="italic"
                style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Built to Scale
              </span>
            </h1>
            <p className="text-[#374151] text-lg max-w-2xl leading-relaxed mb-8">
              From cloud migration and Microsoft 365 deployment to data center management and digital transformation —
              we provide end-to-end IT services that keep your business running efficiently and securely.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <MicrosoftBadge />
              <AzureBadge />
            </div>
            <Link
              to="/book"
              className="group inline-flex items-center gap-2 px-7 py-3.5 bg-[#2563EB] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors duration-300 shadow-sm"
            >
              Get Free Consultation
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Service Blocks */}
      <section className="section-padding bg-[#F6F9FC]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label">Service Portfolio</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A]"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}
            >
              Comprehensive IT Services
            </h2>
          </motion.div>

          <div className="space-y-px bg-[#E2E8F0]">
            {serviceBlocks.map((block, i) => {
              const Icon = block.icon;
              return (
                <motion.div
                  key={block.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white"
                >
                  <div className="p-8 lg:p-10">
                    <div className="grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-16">
                      {/* Left content */}
                      <div>
                        <div className="flex items-center gap-4 mb-5">
                          <div
                            className="w-11 h-11 flex items-center justify-center border shrink-0"
                            style={{ backgroundColor: block.bg, borderColor: `${block.color}30` }}
                          >
                            <Icon size={20} style={{ color: block.color }} />
                          </div>
                          <div>
                            <span className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] block">{block.id}</span>
                            <h3
                              className="font-semibold text-[#0F172A] text-lg"
                              style={{}}
                            >
              {block.title}
              {block.isMicrosoft && (
                <span
                  className="ml-3 inline-flex items-center gap-2 px-2.5 py-1 text-[0.6rem] font-medium tracking-wide uppercase border align-middle rounded"
                  style={{ borderColor: '#00A4EF40', color: '#007AC0', backgroundColor: '#F0FAFF' }}
                >
                  <span style={{ display: 'inline-grid', gridTemplateColumns: '6px 6px', gap: '2px', width: 14, height: 14, flexShrink: 0 }}>
                    <span style={{ background: '#F25022' }} />
                    <span style={{ background: '#7FBA00' }} />
                    <span style={{ background: '#00A4EF' }} />
                    <span style={{ background: '#FFB900' }} />
                  </span>
                  Microsoft Partner
                </span>
              )}
                            </h3>
                          </div>
                        </div>
                        <p className="text-[#374151] text-sm leading-relaxed mb-6 max-w-2xl">
                          {block.description}
                        </p>
                      </div>
                    </div>

                    {/* Offerings grid */}
                    <div>
                      <p className="font-mono text-[0.58rem] tracking-widest text-[#9CA3AF] uppercase mb-4">What we offer</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                        {block.offerings.map((item) => (
                          <div key={item} className="flex items-start gap-2.5">
                            <CheckCircle2 size={14} className="shrink-0 mt-0.5" style={{ color: block.color }} />
                            <span className="text-sm text-[#374151]">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Bottom accent bar */}
                  <div className="h-[1px]" style={{ background: `linear-gradient(90deg, ${block.color}20, transparent)` }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white border-t border-b border-[#E2E8F0] py-12">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0]">
            {[
              { value: '5+', label: 'Service Areas' },
              { value: 'Azure', label: 'Cloud Platform' },
              { value: '24/7', label: 'Monitoring & Support' },
              { value: 'GCC', label: 'Region Coverage' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="bg-white p-8 text-center"
              >
                <p className="font-display font-bold text-[#2563EB] mb-1" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
                  {stat.value}
                </p>
                <p className="text-[#374151] text-xs font-mono tracking-wide uppercase">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={IT_FAQS} theme={IT_THEME} title="IT & Cloud Services FAQs" />

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#2563EB]">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#2563EB]/12 blur-[120px] pointer-events-none" />
        <div className="container-custom py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="font-display font-bold text-white mb-4"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Let's Transform Your{' '}
              <span
                className="italic"
                style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #8da5b8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                IT Infrastructure
              </span>
            </h2>
            <p className="text-[#94A3B8] max-w-xl mb-8 leading-relaxed">
              Whether you're migrating to the cloud, modernizing your Microsoft environment, or planning
              your data center — we're ready to help. Book a free consultation today.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book"
                className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#0F172A] text-sm font-semibold hover:bg-[#F6F9FC] transition-colors duration-300"
              >
                Book Free Consultation
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/20 text-white text-sm font-medium hover:border-white/40 hover:bg-white/5 transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer strip */}
      <div className="bg-[#0F172A] border-t border-[#1E40AF] py-6">
        <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[0.55rem] tracking-widest text-[#334155] uppercase">
            © {new Date().getFullYear()} Smart Solutions · All Rights Reserved
          </p>
          <Link to="/" className="font-mono text-[0.55rem] tracking-widest text-[#334155] hover:text-[#94A3B8] uppercase transition-colors">
            ← Back to main site
          </Link>
        </div>
      </div>

    </div>
  );
};
